<template>
    <ContentComp
        ref="contentComp"
        :height="66"
        :load-state="loadState"
        :show-as-modal="showLoaderAsModal"
        :fill-content="true"
        :loader-label="loaderLabel"
    >
        <SavedChangesMessage
            :visible="showLoaderAsModal && hasBeenSaved"
            class="mx-n3"
        />

        <AlertMessage
            v-if="loadExternalDataFailed"
            class="w-100 flex-fill"
            innerclass="flex-fill"
            type="danger"
        >
            <i18next :translation="$t('externaleventsource.eventsloadfailed')">
                <template #action>
                    <a
                        href="#"
                        @click="loadExternalData"
                        >{{ $tm('general.click') }}</a
                    >
                </template>
            </i18next>
        </AlertMessage>

        <AlertMessage
            v-if="hasValidationErrors"
            type="warning"
            class="mx-n3"
        >
            <span v-html="$tm('externaldata.validationerrors', '', true)"></span>
        </AlertMessage>
        <TabComp
            v-if="!loadExternalDataFailed"
            :tabs="tabs"
            :content-fill="false"
            tab-body-classes="pt-3"
            :pinable="false"
            class="h-100 overflow-auto"
        >
            <template #tab-changes>
                <GridComp
                    ref="unregisteredContestantsGrid"
                    :gridSetup="changedContestantsGridSetup"
                    class="flex-fill"
                    @on-cell-clicked="handleCellClicked"
                    @gridReady="onUnregisteredContestantsGrid"
                />
            </template>

            <template #tab-new>
                <GridComp
                    ref="newContestantsGrid"
                    :gridSetup="newContestantsGridSetup"
                    :resize-strategy="'sizeColumnsToFit'"
                    class="flex-fill"
                />
            </template>
        </TabComp>
    </ContentComp>
</template>
<script setup lang="ts">
/**
 * This handles updates of external data sources (like iSonen)
 *
 * It will present a table with the contestants from the source and what issues they have so you an fix them before importing
 */

import {
    ContestantBase,
    ExternalDatasourceEvent,
    ExternalEventDatasourceResponse,
    ISonenItem,
    RaceContestantDetails,
    UpdateEventRequest,
} from '@/objects/generated';
import ContentComp from '../layout/ContentComp.vue';
import { computed, onMounted, ref, watch } from 'vue';
import { ExternalEventDatasourceDao } from '@/dao';
import { useContestantStore, useContestantStoreRef, useEventCommonStoreRef } from '@/stores';
import { isSameDate, secondsToJsDate } from '@/utilities/dateutils';
import { GridComp, SavedChangesMessage, TabComp } from '..';
import { GridColumnDefinition, GridDataSource, GridSetup, type IGridCompExposed } from '../Grid/Classes';
import type { IGridRowGetterParams } from '../Grid/Classes/GridDataSource';
import { GridColumnOptionsBuilder } from '../Grid/Classes/GridColumnOptions';
import { propertyof } from '@/utilities';
import DisplayClub from '../DisplayClub.vue';
import DisplayParticipantID from './DisplayParticipantID.vue';
import { TaskLoaderState } from '@/composeables';
import { TabContainer, TabDefinition } from '@/objects/Tabs';
import DisplayContestantStatus from '../Participant/DisplayContestantStatus.vue';
import { RaceContestantDetailsExternalData } from '@/objects/RaceContestantDetailsExternalData';
import HandleDestinationCategory from './HandleDestinationCategory.vue';
import HandleSourceCategory from './HandleSourceCategory.vue';
import HandleSourceClub from './HandleSourceClub.vue';
import HandleDestinationClub from './HandleDestinationClub.vue';
import WellKnown from '@/objects/generated/static/WellKnown';
import HandleSourceEMITID from './HandleSourceEMITID.vue';
import HandleDestinationEMITID from './HandleDestinationEMITID.vue';
import HandleDestinationRace from './HandleDestinationRace.vue';
import HandleSourceRace from './HandleSourceRace.vue';
import DisplayTranslation from '../DisplayTranslation.vue';
import AlertMessage from '../Alert/AlertMessage.vue';
import { $td, $ti, $tm } from '@/locize';
import { randomstring } from '@/utilities/utils';
import signalrSubscribe from '@/objects/generated/signalr/signalrSubscribe';
import type { ComponentExposed } from 'vue-component-type-helpers';
import HandleRelayData from './HandleRelayData.vue';
import type { CustomGridState } from '../Grid/Classes/CustomGridState';

const props = defineProps<{
    /**
     * The external data source event to update
     */
    item: ExternalDatasourceEvent | null;

    /**
     * We need the load state to show the loading indicator
     */
    loadState: TaskLoaderState;

    /**
     * We need a AddLoader to add tasks to the parent loader
     */
    addLoader: (loader: () => Promise<void>, key: string, icon?: string | null, noToast?: boolean) => Promise<void>;

    /**
     * We need a ClearLoaders to clear the parent loader
     */
    clearLoaders: () => void;
}>();

// The ref to the content component. We use this to update the loading state and the label of the loading component
const contentComp = ref<ComponentExposed<typeof ContentComp> | null>(null);

// This is set after it has been tried to save
const hasBeenSaved = ref<boolean>(false);

// The grid with new contestants
const newContestantsGrid = ref<IGridCompExposed<RaceContestantDetails> | null>(null);

// The grid with all the changes
const unregisteredContestantsGrid = ref<IGridCompExposed<RaceContestantDetailsExternalData> | null>(null);

// If we want to show as dialog or on top of the page. We show the initial loader on top and then the importer as dialog so we can show the progress
const showLoaderAsModal = ref<boolean>(false);

// The label to show in the loader
const loaderLabel = ref<string>($tm('externaldata.loadingdata'));

// We need the event data to compare with
const { Event, Categories, Races, Groups } = useEventCommonStoreRef();

// We need the contestants to see what is already imported
const { Contestants } = useContestantStoreRef();

const contestantStore = useContestantStore();

// Contains the data we want to import/update
const data = ref<ExternalEventDatasourceResponse | null>(null);

// The contestants from the external source
const contestantsSoure = ref<RaceContestantDetails[]>([]);

// We create a signalr guid so we can receive updates on the import progress
const signalrGUID = randomstring('EU', '');

// Helper to show the errormessage if the load fails
const loadExternalDataFailed = ref<boolean>(false);

/**
 * The currently imported contestants, so we can see if we have duplicates
 */
const existingContestantsISonen = computed<Map<string, ContestantBase>>(() => {
    if (!Contestants.value || Contestants.value.length === 0) return new Map<string, ContestantBase>();

    return new Map(Contestants.value.filter((c) => c.ISonenID && c.ISonenID !== '').map((c) => [c.ISonenID!, c]));
});

/**
 * A list of all the ISonen unique ids from the source so we can check if they are allready imported or not
 */
const newContestantsISonenID = computed(() => {
    return contestantsSoure.value.filter((c) => (c.ISonenID ?? '') !== '').map((c) => c.ISonenID!);
});

// We set up the tabs
const tabs = new TabContainer();
tabs.AddTab(
    new TabDefinition(
        'changes',
        'externaldata.changes',
        'fa-user-pen',
        computed(() => changedContestants.value.length),
    ),
);
tabs.AddTab(
    new TabDefinition(
        'new',
        'externaldata.new',
        'fa-user-plus',
        computed(() => contestantsSoure.value.filter((c) => c.ISonenID && !existingContestantsISonen.value.has(c.ISonenID)).length),
    ),
);

// We need to define the columns for the grid

const gridColumnDefinitions = () => [
    new GridColumnOptionsBuilder<RaceContestantDetails>(propertyof<RaceContestantDetails>('Firstname'), 'import.firstname').Build(),
    new GridColumnOptionsBuilder<RaceContestantDetails>(propertyof<RaceContestantDetails>('Surname'), 'contestant.surnameorrelayteam')
        .ValueGetter((row: RaceContestantDetails) => {
            return row.RelayTeamName && row.RelayTeamName.trim() !== '' ? row.RelayTeamName : (row.Surname ?? '');
        })
        .Build(),
    new GridColumnOptionsBuilder<RaceContestantDetails>(propertyof<RaceContestantDetails>('ClubID'), 'import.club')
        .CellRenderer(DisplayClub)
        .Build(),
    new GridColumnOptionsBuilder<RaceContestantDetails>(
        propertyof<RaceContestantDetails>('ContestantStatusID'),
        'racecontestantcomplete.contestantstatusid',
    )
        .Size(120, 120)
        .CellRenderer(DisplayContestantStatus)
        .Build(),
    new GridColumnOptionsBuilder<RaceContestantDetails>(propertyof<RaceContestantDetails>('CategoryName'), 'general.category').Build(),
    new GridColumnOptionsBuilder<RaceContestantDetails>(propertyof<RaceContestantDetails>('RaceName'), 'general.race').Build(),
    new GridColumnOptionsBuilder<RaceContestantDetails>('ParticipantIDs', 'participantid.emit', 'emit')
        .CellRenderer(DisplayParticipantID, { idtype: 'EMIT' })
        .Build(),
    new GridColumnOptionsBuilder<RaceContestantDetails>('ParticipantIDs', 'participantid.buypass', 'buypass')
        .CellRenderer(DisplayParticipantID, { idtype: 'BUYPASS' })
        .Build(),
    new GridColumnOptionsBuilder<RaceContestantDetails>('ParticipantIDs', 'participantid.nif', 'nif')
        .CellRenderer(DisplayParticipantID, { idtype: 'NIF' })
        .Build(),
];

const newContestants = computed<RaceContestantDetails[]>(() => {
    // we only return the ones that are new and not have id in the existing contestants
    return contestantsSoure.value.filter((c) => c.ISonenID && !existingContestantsISonen.value.has(c.ISonenID));
});

/**
 * The datasource for the new contestants ( i.e not already imported )
 */
const newContestantsDataSource = new GridDataSource<RaceContestantDetails>({
    rowGetter: (params: IGridRowGetterParams<RaceContestantDetails>) => {
        const slice = newContestants.value.slice(params.startRow, params.endRow);

        params.successCallback(slice, newContestants.value, newContestants.value.length);
    },
});

/**
 * The grid setup for the new contestants
 */
const newContestantsGridSetup = new GridSetup<RaceContestantDetails>({
    rowIdGetter: (row: RaceContestantDetails) => row.ISonenID ?? row.ID?.toString() ?? '',
    datasource: newContestantsDataSource,
    hideStatusbar: false,
    GridColumnDefinition: new GridColumnDefinition({
        CommonColumnOptions: {
            sortable: false,
            filterable: false,
            editable: false,
        },
        ColumnOptions: gridColumnDefinitions(),
    }),
    rowSelection: 'single',
});

/**
 * The column definitions for the changes grid
 */
const changesGridColumnDefinitions = () => [
    new GridColumnOptionsBuilder<RaceContestantDetailsExternalData>(
        propertyof<RaceContestantDetailsExternalData>('RaceContestantDetails.Firstname'),
        'import.firstname',
    )
        .SortDisabled()
        .Build(),
    new GridColumnOptionsBuilder<RaceContestantDetailsExternalData>(
        propertyof<RaceContestantDetailsExternalData>('RaceContestantDetails.Surname'),
        'contestant.surnameorrelayteam',
    )
        .ValueGetter((row: RaceContestantDetailsExternalData) => {
            return row.RaceContestantDetails.RelayTeamName && row.RaceContestantDetails.RelayTeamName.trim() !== ''
                ? row.RaceContestantDetails.RelayTeamName
                : (row.RaceContestantDetails.Surname ?? '');
        })
        .SortDisabled()
        .Build(),

    new GridColumnOptionsBuilder<RaceContestantDetailsExternalData>(
        propertyof<RaceContestantDetailsExternalData>('RaceContestantDetails.ContestantStatusID'),
        'racecontestantcomplete.contestantstatusid',
    )
        .SortDisabled()
        .Size(120, 120)
        .CellRenderer(DisplayContestantStatus)
        .Build(),

    new GridColumnOptionsBuilder<RaceContestantDetailsExternalData>(
        propertyof<RaceContestantDetailsExternalData>('RaceContestantDetails.ClubID'),
        'externaldata.sourceclub',
    )
        .CellRenderer(HandleSourceClub)
        .SortDisabled()
        .Size(150)
        .CssRule({
            className: 'cell-indigo',
            func: (val: any, row: RaceContestantDetailsExternalData) => {
                return (row?.RaceContestantDetails.ClubID ?? 0) != (row.RegisteredClubID ?? 0);
            },
        })
        .Build(),
    new GridColumnOptionsBuilder<RaceContestantDetailsExternalData>(
        propertyof<RaceContestantDetailsExternalData>('RegisteredClubID'),
        'externaldata.destinationclub',
    )
        .SortDisabled()
        .Size(150)
        .CellRenderer(HandleDestinationClub)
        .CssRule({
            className: 'cell-indigo',
            func: (val: any, row: RaceContestantDetailsExternalData) =>
                (row?.RaceContestantDetails.ClubID ?? 0) != (row.RegisteredClubID ?? 0),
        })
        .Build(),

    new GridColumnOptionsBuilder<RaceContestantDetailsExternalData>(
        propertyof<RaceContestantDetailsExternalData>('RaceContestantDetails.CategoryName'),
        'externaldata.sourcecategory',
    )
        .CellRenderer(HandleSourceCategory)
        .SortDisabled()
        .CssRule({
            className: 'cell-yellow',
            func: (val: any, row: RaceContestantDetailsExternalData) =>
                row?.RegisteredCategoryName != row.RaceContestantDetails.CategoryName,
        })
        .Build(),

    new GridColumnOptionsBuilder<RaceContestantDetailsExternalData>(
        propertyof<RaceContestantDetailsExternalData>('RegisteredCategoryID'),
        'externaldata.destinationcategory',
    )
        .CellRenderer(HandleDestinationCategory)
        .SortDisabled()
        .CssRule({
            className: 'cell-yellow',
            func: (val: any, row: RaceContestantDetailsExternalData) =>
                row?.RegisteredCategoryName != row.RaceContestantDetails.CategoryName,
        })
        .Build(),

    new GridColumnOptionsBuilder<RaceContestantDetailsExternalData>(
        propertyof<RaceContestantDetailsExternalData>('RaceContestantDetails.RaceName'),
        'externaldata.sourcerace',
    )
        .SortDisabled()
        .CellRenderer(HandleSourceRace)
        .CssRule({
            className: 'cell-orange',
            func: (val: any, row: RaceContestantDetailsExternalData) => row?.RegisteredRaceName != row.RaceContestantDetails.RaceName,
        })
        .Build(),

    new GridColumnOptionsBuilder<RaceContestantDetailsExternalData>(
        propertyof<RaceContestantDetailsExternalData>('RegisteredRaceID'),
        'externaldata.destinationrace',
    )
        .CellRenderer(HandleDestinationRace)
        .SortDisabled()
        .CssRule({
            className: 'cell-orange',
            func: (val: any, row: RaceContestantDetailsExternalData) => row?.RegisteredRaceName != row.RaceContestantDetails.RaceName,
        })
        .Build(),
    new GridColumnOptionsBuilder<RaceContestantDetailsExternalData>(
        'RaceContestantDetails.ParticipantIDs',
        'externaldata.sourceemit',
        'emit',
    )
        .CellRenderer(HandleSourceEMITID)
        .SortDisabled()
        .Size(120, 120)
        .CssRule({
            className: 'cell-cyan',
            func: (val: any, row: RaceContestantDetailsExternalData) => (row?.EMITID ?? '') != '' && row?.RegisteredEMITID != row.EMITID,
        })
        .Build(),

    new GridColumnOptionsBuilder<RaceContestantDetailsExternalData>('RegisteredEMITID', 'externaldata.destinationemit', 'registeredemit')
        .SortDisabled()
        .CellRenderer(HandleDestinationEMITID)
        .Size(120, 120)
        .CssRule({
            className: 'cell-cyan',
            func: (val: any, row: RaceContestantDetailsExternalData) => (row?.EMITID ?? '') != '' && row?.RegisteredEMITID != row.EMITID,
        })
        .Build(),

    new GridColumnOptionsBuilder<RaceContestantDetailsExternalData>(
        'RaceContestantDetails.ParticipantIDs',
        'participantid.buypass',
        'buypass',
    )
        .CellRenderer(DisplayParticipantID, { idtype: 'BUYPASS' })
        .SortDisabled()
        .Build(),
    new GridColumnOptionsBuilder<RaceContestantDetailsExternalData>('RaceContestantDetails.ParticipantIDs', 'participantid.nif', 'nif')
        .CellRenderer(DisplayParticipantID, { idtype: 'NIF' })
        .SortDisabled()
        .Build(),

    // Old and new relayCotnestantCounter
    new GridColumnOptionsBuilder<RaceContestantDetailsExternalData>(
        propertyof<RaceContestantDetailsExternalData>('RaceContestantDetails.RelayTeamName'),
        'externaldata.relayteam',
    )
        .SortDisabled()
        .CellRenderer(HandleRelayData)
        .CssRule({
            className: 'cell-pink',
            func: (val: any, row: RaceContestantDetailsExternalData) => {
                // TODO: If this is a relay team, we first check the number of contestants. If different, we mark as changed
                // if same, we need to check content by serializing the array with relayContestants.
                // We check if there is changes on relaycontesstants
                if (row?.RaceContestantDetails.RelayTeamName?.trim() !== '') {
                    // // We get the existing relayTeamContestants
                    const existingRelayTeamContestants = contestantStore.GetRelayContestants(row.RaceContestantDetails.ContestantID);

                    if (existingRelayTeamContestants.length != row.RaceContestantDetails.RelayTeamContestants.length) {
                        return true;
                    }

                    // We need to check content
                    const jsonExisting = JSON.stringify(
                        existingRelayTeamContestants
                            .map((rc) => ({
                                Firstname: rc.Firstname,
                                Surname: rc.Surname,
                                LegNumber: rc.LegNumber,
                                Chip1: rc.Chip1,
                                Chip2: rc.Chip2,
                            }))
                            .sort((a, b) => (a.LegNumber ?? 0) - (b.LegNumber ?? 0)),
                    );

                    const jsonNew = JSON.stringify(
                        row.RaceContestantDetails.RelayTeamContestants.map((rc) => ({
                            Firstname: rc.Firstname,
                            Surname: rc.Surname,
                            LegNumber: rc.LegNumber,
                            Chip1: rc.Chip1,
                            Chip2: rc.Chip2,
                        })).sort((a, b) => (a.LegNumber ?? 0) - (b.LegNumber ?? 0)),
                    );

                    if (jsonExisting !== jsonNew) {
                        return true;
                    }
                }
                return false;
            },
        })
        .Build(),

    new GridColumnOptionsBuilder<RaceContestantDetailsExternalData>('ErrorMessage', 'externaldata.status')
        .CellRenderer(DisplayTranslation)
        .SortDisabled()
        .Build(),
];

/**
 * A list of all the contestants that have changes or are unregistered
 */
const changedContestants = computed<RaceContestantDetailsExternalData[]>(() => {
    // First we find the unregistered contestants
    // we only return the ones that are new and not have id in the existing contestants
    // We convert them to RaceContestantDetails
    const unregistered = Contestants.value
        .filter((c) => (c.ISonenID ?? '') != '' && !newContestantsISonenID.value.includes(c.ISonenID ?? '') && c.ContestantStatusID == 1)
        .map((c) => RaceContestantDetailsExternalData.FromContestantBase(c));

    unregistered.forEach((c) => {
        const existing = existingContestantsISonen.value.get(c.RaceContestantDetails.ISonenID ?? '');
        if (!existing) return;

        c.RaceContestantDetails.ContestantStatusID = 9; // Set to unregistered / Avmeldt
        //c.RaceContestantDetails.ID = existing.ID;
        c.RaceContestantDetails.ParticipantID = existing!.ParticipantID;
        c.RegisteredClubID = existing!.ClubID;
        c.RaceContestantDetails.ClubID = existing!.ClubID;
        c.RegisteredCategoryID = existing!.CategoryID;
        c.RegisteredCategoryName = Categories.value.find((cat) => cat.ID === existing!.CategoryID)?.Name ?? null;
        c.RaceContestantDetails.CategoryName = c.RegisteredCategoryName;
        c.RegisteredRaceID = existing!.RaceContestants.at(0)?.RaceID ?? null;
        c.RegisteredRaceName = Races.value.find((r) => r.ID === c.RegisteredRaceID)?.Name ?? null;
        c.RaceContestantDetails.RaceName = c.RegisteredRaceName;
    });

    // Now we handle the ones that are registered but have changes in either
    // Club, EMIT ID, Category or Race. We need to iterate over all contestants from the external source
    // and compare with the contestant in the contestantStore

    // We need to iterate all the contestants from the source
    contestantsSoure.value.forEach((src) => {
        // We need to find the contestant in the existing contestants
        const existing = existingContestantsISonen.value.get(src.ISonenID ?? '');
        if (!existing) return;

        const existingcat = Categories.value.find((c) => c.ID === existing.CategoryID);

        const newSrc = RaceContestantDetailsExternalData.FromRaceContestantDetails(src); // Not found, so it's a new contestant
        newSrc.RegisteredClubID = existing.ClubID; // We set the registered club to the existing one
        newSrc.RegisteredCategoryID = existing.CategoryID; // We set the registered category to the existing one
        newSrc.RegisteredCategoryName = existingcat?.Name ?? null;
        newSrc.RegisteredRaceID = existing.RaceContestants.at(0)?.RaceID ?? null;
        newSrc.RegisteredRaceName = Races.value.find((r) => r.ID === newSrc.RegisteredRaceID)?.Name ?? null;
        newSrc.RaceContestantDetails.ContestantID = existing.ID;
        newSrc.RaceContestantDetails.ParticipantID = existing.ParticipantID;

        newSrc.EMITID = src.ParticipantIDs?.find((id) => id.ParticipantIDTypeID === WellKnown.IDTYPE_EMIT)?.Value ?? null;
        newSrc.RegisteredEMITID = existing.ParticipantIDs?.find((id) => id.ParticipantIDTypeID === WellKnown.IDTYPE_EMIT)?.Value ?? null;

        // newSrc.ErrorMessage = validateItem(newSrc);

        if (src.ClubID !== newSrc.RegisteredClubID) {
            unregistered.push(newSrc);
            return;
        }

        if (existingcat && newSrc.RaceContestantDetails.CategoryName !== newSrc.RegisteredCategoryName) {
            unregistered.push(newSrc);
            return;
        }

        // We check if the emit id is included and has changed
        if ((newSrc.EMITID ?? '') != '' && newSrc.EMITID?.trim() !== newSrc.RegisteredEMITID?.trim()) {
            unregistered.push(newSrc);
            return;
        }

        // We check if the Race has changed
        if (newSrc.RaceContestantDetails.RaceName !== newSrc.RegisteredRaceName) {
            unregistered.push(newSrc);
            return;
        }

        // We check if there is changes on details, i.e name, team name, contact details etc.

        // We check if there is changes on relaycontesstants
        if (newSrc.RaceContestantDetails.RelayTeamName?.trim() !== '') {
            // We get the existing relayTeamContestants
            const existingRelayTeamContestants = contestantStore.GetRelayContestants(existing.ID!);

            // We only check if the length of the relayteamcontestants differ
            if (newSrc.RaceContestantDetails.RelayTeamContestants.length != existingRelayTeamContestants.length) {
                unregistered.push(newSrc);
                return;
            } else {
                // We need to check content
                const jsonExisting = JSON.stringify(
                    existingRelayTeamContestants
                        .map((rc) => ({
                            Firstname: rc.Firstname,
                            Surname: rc.Surname,
                            LegNumber: rc.LegNumber,
                            Chip1: rc.Chip1,
                            Chip2: rc.Chip2,
                        }))
                        .sort((a, b) => (a.LegNumber ?? 0) - (b.LegNumber ?? 0)),
                );

                const jsonNew = JSON.stringify(
                    newSrc.RaceContestantDetails.RelayTeamContestants.map((rc) => ({
                        Firstname: rc.Firstname,
                        Surname: rc.Surname,
                        LegNumber: rc.LegNumber,
                        Chip1: rc.Chip1,
                        Chip2: rc.Chip2,
                    })).sort((a, b) => (a.LegNumber ?? 0) - (b.LegNumber ?? 0)),
                );

                if (jsonExisting !== jsonNew) {
                    unregistered.push(newSrc);
                    return;
                }
            }
        }

        if (!compareBaseData(existing, src)) {
            unregistered.push(newSrc);
            return;
        }
    });

    return unregistered;
});

/**
 * We need to compare the important values to see what we want to update
 * @param {ContestantBase} existing
 * @param {RaceContestantDetails} source
 * @returns {boolean} true if they are the same, false if they are different
 */
const compareBaseData = (existing: ContestantBase, source: RaceContestantDetails): boolean => {
    // Check Buypassid og personId/NIFID too?
    const buypassid = existing.ParticipantIDs?.find((id) => id.ParticipantIDTypeID === WellKnown.IDTYPE_BUYPASS_ID)?.Value ?? null;
    const nifid = existing.ParticipantIDs?.find((id) => id.ParticipantIDTypeID === WellKnown.IDTYPE_NIF)?.Value ?? null;

    const sourceBuypassID = source.ParticipantIDs?.find((id) => id.ParticipantIDTypeID === WellKnown.IDTYPE_BUYPASS_ID)?.Value ?? null;
    const sourceNIFID = source.ParticipantIDs?.find((id) => id.ParticipantIDTypeID === WellKnown.IDTYPE_NIF)?.Value ?? null;

    // If we match on a ID we know it is the same person
    if ((buypassid ?? '') != '' && buypassid?.trim() === sourceBuypassID?.trim()) return true;
    if ((nifid ?? '') != '' && nifid?.trim() === sourceNIFID?.trim()) return true;

    if (existing.Firstname?.trim() !== source.Firstname?.trim()) return false;
    if ((source.RelayTeamName ?? '') === '' && existing.Surname?.trim() !== source.Surname?.trim()) return false;
    if (source.RelayTeamName && source.RelayTeamName.trim() !== '' && existing.RelayTeamName?.trim() !== source.RelayTeamName.trim())
        return false;
    if (existing.Gender !== source.Gender) return false;
    if (existing.Birthday?.toISOString().split('T')[0] !== source.Birthday?.toISOString().split('T')[0]) return false;
    // if (existing.NationalityCountryID !== source.NationalityCountryID) return false;
    return true;
};

/**
 * Validate a single item to see if it is valid for import
 *
 * @param {RaceContestantDetailsExternalData} item
 * @returns {string | null} null if valid, error message key if not valid
 */
const validateItem = (item: RaceContestantDetailsExternalData): string | null => {
    // We need to check if the current item is valid for import.

    // We ignore the checks for "Avmeldt"
    if (item.RaceContestantDetails.ContestantStatusID == 9) return null;

    // We need to compare to see that categories, groups and races are correct and match the event

    // We need to check what category it tries to import to
    let categoryName = item.UseExternalCategory ? item.RaceContestantDetails.CategoryName : item.RegisteredCategoryName;
    let RaceName = item.UseExternalRace ? item.RaceContestantDetails.RaceName : item.RegisteredRaceName;

    // We need to check that this matches categories in the event
    const category = Categories.value.find((c) => c.Name === categoryName);
    const race = Races.value.find((r) => r.Name === RaceName);

    if (!category || !race) {
        return 'externaldata.novalidcategoryorrace';
    }

    // We need to find the groups of the race
    const groups = Groups.value.filter((g) => g.RaceID == race.ID);
    if (groups.length == 0) {
        return 'externaldata.nomatchingcategoryinrace';
    }

    // We need a list of all the category names in the group.categoryconnection
    let categoryIDs = groups.flatMap((g) => g.CategoryGroupConnection.map((c) => c.CategoryID));
    let categoryNames = Categories.value.filter((c) => categoryIDs.includes(c.ID)).map((c) => c.Name);

    if (!categoryNames.includes(categoryName ?? '')) {
        return 'externaldata.nomatchingcategoryrace';
    }
    return null;
};

/**
 * Do we have any validation errors in the changed contestants
 * We use this to show a warning message in the top
 */
const hasValidationErrors = ref<boolean>(false);

const validateAllItems = () => {
    changedContestants.value.forEach((c) => {
        c.ErrorMessage = validateItem(c);
    });

    hasValidationErrors.value = changedContestants.value.some((c) => c.ErrorMessage != null);

    unregisteredContestantsGrid.value?.refreshCachedData();
};

watch(changedContestants, (nv, ov) => {
    validateAllItems();
});

const handleCellClicked = (rowid: string, colid: string) => {
    // We only handle cell clicked on colid
    const handleCols = [
        'ClubID',
        'RegisteredClubID',
        'CategoryName',
        'RegisteredCategoryID',
        'RaceName',
        'RegisteredRaceID',
        'emit',
        'registeredemit',
    ];
    if (!handleCols.includes(colid)) return;

    validateAllItems();
};

/**
 * The datasource for the new contestants ( i.e not already imported )
 */
const changedContestantsDataSource = new GridDataSource<RaceContestantDetailsExternalData>({
    rowGetter: (params: IGridRowGetterParams<RaceContestantDetailsExternalData>) => {
        const slice = changedContestants.value.slice(params.startRow, params.endRow);

        params.successCallback(slice, changedContestants.value, changedContestants.value.length);
    },
});

/**
 * The grid setup for the new contestants
 */
const changedContestantsGridSetup = new GridSetup<RaceContestantDetailsExternalData>({
    rowIdGetter: (row) => row.RaceContestantDetails.ISonenID ?? '',
    datasource: changedContestantsDataSource,
    hideStatusbar: false,
    GridColumnDefinition: new GridColumnDefinition({
        CommonColumnOptions: {
            sortable: false,
            filterable: false,
            editable: false,
        },
        ColumnOptions: changesGridColumnDefinitions(),
    }),
    rowSelection: 'multiple',
    hasCheckboxSelectionColumn: true,
    rowClassRules: [
        {
            className: 'grid-row-error',
            func: (data: RaceContestantDetailsExternalData) => data.ErrorMessage != null,
        },
    ],
});

/**
 * Triggered when the changdeDate is loaded into the grid. We select all rows by default so you have to deselect what you dont want to import
 * @param state
 */
const onUnregisteredContestantsGrid = (state: CustomGridState | null) => {
    // We select all rows by default
    unregisteredContestantsGrid.value?.selectAllRows();
};

/**
 * This will load the external data from the source
 */
const loadExternalData = async () => {
    loadExternalDataFailed.value = false;
    if (!props.item || !Event.value) return;

    props.clearLoaders();

    // Since we for now only support isonen, we just load that
    const isonenItem = ISonenItem.FromJsValues({
        Name: `${Event.value?.ID}`,
        DescriptiveName: Event.value?.Name ?? '',
        OrganizerIDs: [Event.value.OrganizerID],
    });

    await props.addLoader(async () => {
        await contestantStore.EnsureLoaded(['Contestants', 'RelayContestants']);

        let responses: ExternalEventDatasourceResponse[] | null = null;
        try {
            responses = (await ExternalEventDatasourceDao.GetEventDatasourceResponse(isonenItem, props.item!.Key, true)) ?? null;
        } catch (e) {
            loadExternalDataFailed.value = true;
            return;
        }

        // If we have multiple responses we need to find the one for this event ( Here we use the event data )
        data.value =
            responses.length == 1
                ? responses[0]
                : (responses?.find((r) => r.EventInfo && r.EventInfo.Date && isSameDate(r.EventInfo.Date, Event.value!.Date)) ?? null);

        if (!data.value) return;

        contestantsSoure.value = (data.value?.Contestants ?? []).sort((a, b) => {
            // First sort on RaceName, then on CategoryName, then on Surname, then on Firstname
            if (a.RaceName !== b.RaceName) return (a.RaceName ?? '').localeCompare(b.RaceName ?? '');
            if (a.CategoryName !== b.CategoryName) return (a.CategoryName ?? '').localeCompare(b.CategoryName ?? '');
            if (a.Surname !== b.Surname) return (a.Surname ?? '').localeCompare(b.Surname ?? '');
            return (a.Firstname ?? '').localeCompare(b.Firstname ?? '');
        });

        showLoaderAsModal.value = true;
        loaderLabel.value = '';
    }, 'externaldata.loading');
};

/**
 * Exposed method to get all the dataset after it has been updated, so it is ready for import
 */
const GetUpdateEventRequest = (): UpdateEventRequest | null => {
    if (data.value == null) return null;

    // If something failed on the external data, we cannot save anything
    if (loadExternalDataFailed.value) return null;

    const request = UpdateEventRequest.FromJsValues({
        ImportFormat: data.value.ImportFormat,
        EventInfo: data.value.EventInfo,
        EventSetup: data.value.EventSetup,
        SignalrKey: signalrGUID,
    });

    // We only import the contestants that are either changed or new or marked as unregistered
    const selectedChangedContestants = (unregisteredContestantsGrid.value?.getSelectedRows() ?? []).map(
        (r) => r.RaceContestantDetails.ISonenID ?? '',
    );

    request.Contestants =
        changedContestants.value
            .filter(
                (x) =>
                    selectedChangedContestants.includes(x.RaceContestantDetails.ISonenID ?? '') &&
                    x.ErrorMessage == null &&
                    (x.UseExternalCategory ||
                        x.UseExternalClub ||
                        x.UseExternalEMITID ||
                        x.UseExternalRace ||
                        x.RaceContestantDetails.ContestantStatusID == 9),
            )
            .map((c) => c.ToRaceContestantDetails())
            .concat(newContestants.value) ?? [];
    return request;
};

// We set up a signalr handler with a unique GUID
signalrSubscribe.ContestantImportRowStatus({ ImportGuid: signalrGUID }, (status) => {
    let statusText = status[0].Status ?? '';
    let totalRows = status[0].TotalRows;
    let currentRows = status[0].CurrentRow;
    let statusValues = { currentRow: currentRows, totalRows: totalRows, elapsedTime: '', timeRemaining: '', failedRows: 0 };
    // If we dont have a statusText there is nothing to show.
    if (statusText == '') return;

    // is running
    if (currentRows > 0) {
        if (totalRows == currentRows) {
            // done
            statusValues.elapsedTime = $td(secondsToJsDate(status[0].ElapsedTime! / 1000), 'TIME');
            statusValues.failedRows = status[0].FailedRows!;
            statusValues.totalRows = totalRows - status[0].FailedRows!;
        } else {
            // importing
            let diffRows = totalRows - currentRows;
            let interval = diffRows > 1000 ? 100 : diffRows > 100 ? 10 : 1;

            statusValues.currentRow = Math.floor(currentRows / interval) * interval;
            statusValues.timeRemaining = $td(secondsToJsDate((status[0].AverageTimePrRow! * (totalRows - currentRows)) / 1000), 'TIME');
        }
    }
    contentComp.value?.loadingComp?.setLabel($ti(status[0].Status!, statusValues, true));
});

/**
 * Whenever the source data changes we need to refresh the grids
 */
watch(contestantsSoure, () => {
    newContestantsGrid.value?.refreshCachedData();
});

onMounted(async () => {
    await loadExternalData();
});

defineExpose({
    /**
     * Returns the update event request with all the changes made
     */
    GetUpdateEventRequest,

    SetSaveSuccess: () => {
        hasBeenSaved.value = true;
    },
});
</script>
